import { DocPageHeading } from '@squareup/dex-feature-docs-page';
import HomeIcon from '@squareup/dex-icons/dex/misc/Home';
import { DocPage, NavSet, TocSection } from '@squareup/dex-types-shared-docs';
import { Head } from '@squareup/dex-ui';
import { DocsPageContentLayout } from '@squareup/dex-ui-dex-tech-docs-page-layout';
import { Box } from '@squareup/dex-ui-shared-base';
import { MarkdownDocument } from '@squareup/dex-ui-shared-markdown-components';
import {
  MarkdownToc,
  MarkdownTocProvider,
} from '@squareup/dex-ui-shared-markdown-toc';
import { DocsFloatingToc } from '@squareup/dex-ui-shared-table-of-contents';
import {
  getAncestorNavItemsForTarget,
  makeDocUrlRelative,
} from '@squareup/dex-utils-docs-navigation';
import { getNextPublicDocsNameOrDefault } from '@squareup/dex-utils-environment';
import { MDXRemote, MDXRemoteSerializeResult } from 'next-mdx-remote';
import React, { FC, useMemo, useRef } from 'react';

import { getMdxComponentMap } from './mdx-component-map';
import styles from './mdx-content.module.css';

function getDocsCanonicalPath(slug: string): string {
  // Production `/docs/homepage` canonicalizes to `/docs`; keep parity until the
  // empty-slug MDX cutover wires `/docs/` itself.
  return slug === 'homepage' ? '/docs' : `/docs/${slug}`;
}

interface MdxPageMetadata {
  title: string;
  browserTitle?: string;
  slug: string;
  summary?: string;
  metaDescription?: string;
  summaryPlacement?: 'body-after-applies-to';
  releaseStatus?: string;
  hideToc?: boolean;
  pageType?: 'landing';
  tocSections?: Array<TocSection>;
}

interface MdxPageContentProps {
  mdxSource: MDXRemoteSerializeResult;
  metadata: MdxPageMetadata;
  navSet?: NavSet | undefined;
}

const MdxPageContent: FC<MdxPageContentProps> = ({
  mdxSource,
  metadata,
  navSet,
}) => {
  // Use the same breadcrumb trail Contentful pages render: walk the navSet
  // for the current slug, take the ancestor subcategories, and prepend the
  // home icon. Without the navSet we fall back to a slug-derived trail so
  // local dev / payload-only previews don't drop breadcrumbs entirely, but
  // the production path always has navSet (it's bundled into the payload).
  const breadcrumbItems = useMemo(() => {
    const homeBreadcrumb = {
      href: '/docs',
      title: <HomeIcon />,
      label: 'Documentation homepage',
    };
    if (navSet) {
      const ancestors =
        getAncestorNavItemsForTarget(navSet, `/docs/${metadata.slug}`) || [];
      if (ancestors.length === 0) {
        return [];
      }
      const items = ancestors.map((item) => ({
        href: makeDocUrlRelative(item.url),
        title: item.title,
      }));
      // Drop adjacent ancestors that resolve to the same URL. The MDX
      // nav.yml lets a section sit at the same slug as its parent tab
      // (e.g. tab "Build Basics" at `build-basics` wraps a "Build Basics"
      // section also at `build-basics`), which would otherwise render as
      // `Home / Build Basics / Build Basics` — Contentful's nav schema
      // collapses this and we should match that visual.
      const deduped = items.filter(
        (item, i) => i === 0 || item.href !== items[i - 1]?.href
      );
      return [homeBreadcrumb, ...deduped];
    }
    const segments = metadata.slug.split('/');
    if (segments.length < 2) {
      return [];
    }
    const ancestors = segments.slice(0, -1);
    return [
      homeBreadcrumb,
      ...ancestors.map((seg, i) => ({
        href: `/docs/${ancestors.slice(0, i + 1).join('/')}`,
        title: seg
          .split(/[_-]/)
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' '),
      })),
    ];
  }, [metadata.slug, navSet]);

  const heading = useMemo<
    Partial<Pick<DocPage['heading'], 'summary' | 'title' | 'releaseStatus'>>
  >(() => {
    const renderSummaryInBody =
      metadata.summaryPlacement === 'body-after-applies-to';
    return {
      title: metadata.title,
      ...(metadata.summary &&
      !renderSummaryInBody &&
      metadata.pageType !== 'landing'
        ? { summary: metadata.summary }
        : {}),
      ...(metadata.releaseStatus
        ? {
            releaseStatus:
              metadata.releaseStatus as DocPage['heading']['releaseStatus'],
          }
        : {}),
    };
  }, [
    metadata.pageType,
    metadata.releaseStatus,
    metadata.summary,
    metadata.summaryPlacement,
    metadata.title,
  ]);

  // Match the Contentful path's TOC rules so MDX pages get the same
  // "On this page" rail: keep level 1 + 2 only (h2s — h3 sub-sections
  // would create visual noise) and only show the rail when there are at
  // least two anchors to jump to. Sections come from the precompiled
  // payload (extractTocSections walks the MDX source).
  const ftocSections = useMemo(
    () => (metadata.tocSections || []).filter((section) => section.level < 3),
    [metadata.tocSections]
  );
  const ftocEnabled = !metadata.hideToc && ftocSections.length > 1;
  const markdownContentRef = useRef<HTMLElement>(null);
  const usesInlineToc = metadata.summaryPlacement === 'body-after-applies-to';

  // Memoize so MDXRemote's `components` prop has stable identity. Without
  // this, every parent re-render produces a fresh component map, MDXRemote
  // remounts its subtree, and any local state in adapter components
  // (notably MdxTabs's `currentTabId`) snaps back to its initial value —
  // which makes tab clicks appear to do nothing.
  const components = useMemo(
    () => ({
      ...getMdxComponentMap(),
      MdxInlineToc: () =>
        ftocEnabled ? <MarkdownToc depth={2} hide={true} /> : null,
    }),
    [ftocEnabled]
  );

  const rightSidebar = ftocEnabled ? (
    <DocsFloatingToc
      headerTitle="On this page"
      sections={ftocSections}
      mainContentRef={markdownContentRef}
    />
  ) : undefined;

  return (
    <DocsPageContentLayout
      rightSidebar={rightSidebar}
      gridType={ftocEnabled ? 'ftoc' : undefined}
    >
      <>
        <Head
          title={
            metadata.browserTitle ||
            metadata.title ||
            getNextPublicDocsNameOrDefault()
          }
          description={metadata.metaDescription || metadata.summary || ''}
          canonicalPath={getDocsCanonicalPath(metadata.slug)}
        />
        <DocPageHeading heading={heading} breadcrumbItems={breadcrumbItems} />
        <Box className={styles['content']} ref={markdownContentRef}>
          {/* MarkdownDocument applies the `.article` styles
              (img max-width: 100%, list-style, paragraph spacing, etc.)
              that the Contentful render path uses. Without this wrap,
              MDX content renders with browser-default <ul>/<img> styling
              — bullets unindented, screenshots full natural size.
              `.content` (mdx-content.module.css) layers on top to make
              markdown-syntax images fill the column by default — most
              docs images are screenshots, and `.article img` only caps
              max width so smaller-than-column images looked too small
              vs Contentful's render. Per-image override:
              `<img src=... width=... />` JSX still wins. */}
          <MarkdownDocument
            frontmatter={
              metadata.pageType ? { pageType: metadata.pageType } : undefined
            }
          >
            <MarkdownTocProvider value={{ sections: ftocSections }}>
              {ftocEnabled && !usesInlineToc && (
                <MarkdownToc depth={2} hide={true} />
              )}
              <MDXRemote {...mdxSource} components={components} />
            </MarkdownTocProvider>
          </MarkdownDocument>
        </Box>
      </>
    </DocsPageContentLayout>
  );
};

MdxPageContent.displayName = 'MdxPageContent';

export { MdxPageContent };
export type { MdxPageMetadata, MdxPageContentProps };
